<template>
  <div
    ref="carouselRef"
    class="relative overflow-hidden"
    :class="containerClass"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
  >
    <!-- Slides -->
    <div class="relative h-full">
      <div
        v-for="(slide, index) in slides"
        :key="index"
        class="absolute inset-0 transition-opacity duration-500"
        :class="[
          currentIndex === index ? 'opacity-100 z-10' : 'opacity-0 z-0',
          slideClass
        ]"
      >
        <slot :slide="slide" :index="index" :active="currentIndex === index">
          <img
            v-if="typeof slide === 'string'"
            :src="slide"
            class="w-full h-full object-cover"
            :alt="`Slide ${index + 1}`"
          />
        </slot>
      </div>
    </div>

    <!-- Navigation Arrows -->
    <button
      v-if="showArrows && slides.length > 1"
      @click="prev"
      class="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 inline-flex items-center justify-center rounded-full bg-background/80 text-foreground backdrop-blur-sm border border-border hover:bg-background transition-colors duration-150"
      aria-label="Anterior"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    <button
      v-if="showArrows && slides.length > 1"
      @click="next"
      class="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 inline-flex items-center justify-center rounded-full bg-background/80 text-foreground backdrop-blur-sm border border-border hover:bg-background transition-colors duration-150"
      aria-label="Seguinte"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <!-- Indicators -->
    <div
      v-if="showIndicators && slides.length > 1"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-1.5"
    >
      <button
        v-for="(_, index) in slides"
        :key="index"
        @click="goTo(index)"
        class="h-1.5 rounded-full transition-all duration-200"
        :class="currentIndex === index ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75 w-1.5'"
        :aria-label="`Ir para slide ${index + 1}`"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  slides: {
    type: Array,
    required: true
  },
  autoPlay: {
    type: Boolean,
    default: true
  },
  interval: {
    type: Number,
    default: 3000
  },
  showArrows: {
    type: Boolean,
    default: true
  },
  showIndicators: {
    type: Boolean,
    default: false
  },
  containerClass: {
    type: String,
    default: ''
  },
  slideClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['change'])

const currentIndex = ref(0)
const carouselRef = ref(null)
let timer = null
let touchStartX = 0

const next = () => {
  currentIndex.value = (currentIndex.value + 1) % props.slides.length
  emit('change', currentIndex.value)
}

const prev = () => {
  currentIndex.value = (currentIndex.value - 1 + props.slides.length) % props.slides.length
  emit('change', currentIndex.value)
}

const goTo = (index) => {
  currentIndex.value = index
  emit('change', currentIndex.value)
}

const handleTouchStart = (e) => {
  touchStartX = e.touches[0].clientX
}

const handleTouchEnd = (e) => {
  const touchEndX = e.changedTouches[0].clientX
  const diff = touchStartX - touchEndX

  if (Math.abs(diff) > 50) {
    if (diff > 0) {
      next()
    } else {
      prev()
    }
  }
}

const startAutoPlay = () => {
  if (props.autoPlay && props.slides.length > 1) {
    timer = setInterval(next, props.interval)
  }
}

const stopAutoPlay = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

watch(() => props.autoPlay, (val) => {
  if (val) {
    startAutoPlay()
  } else {
    stopAutoPlay()
  }
})

onMounted(() => {
  startAutoPlay()
})

onUnmounted(() => {
  stopAutoPlay()
})

defineExpose({ next, prev, goTo, currentIndex })
</script>
